import {DropdownActionMenuItem, DropdownActionProps} from 'platform/components';
import {foundationTheme, ThemeIconKey, ThemeLottieKey} from 'platform/foundation';

import {partition, splitAt} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import type {EntityHeaderAction, DropdownPosition} from '../types';

const dropdownActionTypes: DropdownActionProps['type'][] = [
  'dropdown-button',
  'dropdown-iconButton',
];

const isThemeLottieKey = (
  value: ThemeIconKey | ThemeLottieKey | undefined
): value is ThemeLottieKey => value !== undefined && value in foundationTheme.lotties;

const isThemeIconKey = (value: ThemeIconKey | ThemeLottieKey | undefined): value is ThemeIconKey =>
  value !== undefined && !isThemeLottieKey(value);

const isDropdownAction = (action: EntityHeaderAction): action is DropdownActionProps =>
  dropdownActionTypes.includes(action.type);

const isIconButtonDropdown = (action: DropdownActionProps) => action.type === 'dropdown-iconButton';

const NOOP_HANDLER = () => {};

const actionToMenuItem = (action: EntityHeaderAction): DropdownActionMenuItem | null => {
  if (action.type === 'button' || action.type === 'buttonWithTooltip') {
    return {
      label: action.title,
      onClick: action.onClick ?? NOOP_HANDLER,
      leftIcon: isThemeIconKey(action.leftIcon) ? action.leftIcon : undefined,
      isDisabled: action.isDisabled,
      isLoading: action.isLoading,
      'data-testid': action['data-testid'],
    };
  }

  return null;
};

export function getResponsiveActions(
  actions: EntityHeaderAction[],
  maxVisibleActions: number,
  dropdownPosition: DropdownPosition
): EntityHeaderAction[] {
  if (isNilOrEmpty(actions)) {
    return [];
  }

  const [dropdownActions, nonDropdownActions] = partition(isDropdownAction, actions);

  const [actionsToShow, actionsToHide] = splitAt(maxVisibleActions, nonDropdownActions);
  const [iconDropdowns, buttonDropdowns] = partition(isIconButtonDropdown, dropdownActions);

  const mergedIconDropdown: DropdownActionProps | null =
    iconDropdowns.length > 0
      ? {
          ...iconDropdowns[0],
          menuItems: iconDropdowns.flatMap((dropdown) => dropdown.menuItems),
        }
      : null;

  if (isNilOrEmpty(actionsToHide)) {
    const dropdown = mergedIconDropdown ? [mergedIconDropdown] : [];

    return dropdownPosition === 'start'
      ? [...dropdown, ...buttonDropdowns, ...actionsToShow]
      : [...actionsToShow, ...buttonDropdowns, ...dropdown];
  }

  const hiddenMenuItems: DropdownActionMenuItem[] = actionsToHide
    .map(actionToMenuItem)
    .filter((item) => item !== null);

  const dropdownWithActionsToHide: DropdownActionProps = mergedIconDropdown
    ? {
        ...mergedIconDropdown,
        menuItems: [...hiddenMenuItems, ...mergedIconDropdown.menuItems],
      }
    : {
        type: 'dropdown-iconButton',
        priority: 'secondary',
        menuItems: hiddenMenuItems,
      };

  return dropdownPosition === 'start'
    ? [dropdownWithActionsToHide, ...buttonDropdowns, ...actionsToShow]
    : [...actionsToShow, ...buttonDropdowns, dropdownWithActionsToHide];
}
